import { isNative, writeDataToValueStore } from "../JWeb";
import {
  DataCollectionEventNames,
  AssetUnitType,
  ReservedValueStoreKeys,
  Stack,
} from "../JWeb/JWebEnums";
import { CDMEventData, DataValveCDMEventData, Publisher } from "../JWeb/types";

import {
  DataValveCDMEvent,
  GenericPublisher,
  PublishCdmEventsOptions,
  AnalyticsServiceInputType,
} from "./types";

import { getStratusStack, hasErrorInEventServicePublisher } from "./utils";
import VisitUuidHandler from "./visitUuidHandler";

import {
  DataCollectionRecorderWeb,
  DataCollectionRecorderNative,
} from "./DataCollectionRecorder";
import { jWebReady, jWebStates } from "../JWeb/initializer";

import * as JwebEventService from "../JWeb/eventService";
import { getOptanonConsentObjectValues } from "../consentService/optanonUtils";
import {
  PublishStepsEnum,
  thirtyMinutesInSeconds,
  visitUuidCookieName,
  zeroedUUID,
} from "../../../constants";
import { injectOneTrust } from "../../../oneTrust";
import { decodeToken } from "../../../utils";
import ConsentService from "../consentService/ConsentService";
import IAnalyticsService from "./IAnalyticsService";
import bindAllMethods from "../../utils/bindAllMethods";
import { getCookie, setCookie } from "../../../cookies";
import { getRandomOrZeroedUUID } from "../../../crypto";
import getFlatMap from "../../utils/getFlatMap";
import { startSpan } from "../../utils/spunkRum";
import { PublishCdmEventsProps } from "../../../types";

export const OPT_IN_GROUP_VALUE = "2:1";
export const OPT_OUT_GROUP_VALUE = "2:0";
export const CDM_VERSION = "1.0.0.0";

async function _publishWeb(
  publisherWeb: Publisher,
  eventName: string,
  eventData: any,
  stack: string
): Promise<void> {
  // local testing purposes
  if (getStratusStack(stack) === Stack.local) {
    console?.warn?.(
      "Analytics: Event(s) being sent without OptInGroup validation. (Local environment)"
    );
    await publisherWeb.publish(eventName, eventData);
  } else {
    // if isNative it should publish even optanonConsentId is zeroed
    if (
      getOptanonConsentObjectValues().optanonConsentId !== zeroedUUID ||
      (await isNative())
    ) {
      await publisherWeb.publish(eventName, eventData);
    } else {
      console?.warn?.("Analytics: Event(s) not sent. No AnalyticsOptInGroup.");
    }
  }
}

export default class AnalyticsService implements IAnalyticsService {
  private _isEnabled = true;
  private _dataCollectionRecorder!:
    | DataCollectionRecorderNative
    | DataCollectionRecorderWeb; /// Only for Web
  private _visitUuidHandler: any;
  private _version = CDM_VERSION;
  private _publisher!: GenericPublisher | any;
  private _stack: string = "dev";
  private inited = false;
  private assetType: string | undefined;
  private publisherId!: string;
  private _authProvider: any;
  private _consentService: any;
  public debuggedEvents: any = [];
  public subscribedEvents: any = [];
  public cachedEventsBeforeConsent: PublishCdmEventsProps[] = [];
  public currentEvent: any;

  public mountFrominit(analyticsProps: AnalyticsServiceInputType) {
    const { stack, enableOneTrust, assetType, authProvider } = analyticsProps;

    if (enableOneTrust) injectOneTrust();
    this._stack = Stack[stack];
    this.assetType = assetType;
    this._authProvider = authProvider;

    this._consentService = new ConsentService({
      handleConsentChange: () => this.handleConsentChange(this),
    });
    this._visitUuidHandler = new VisitUuidHandler(this);
    this._dataCollectionRecorder = new DataCollectionRecorderWeb(
      analyticsProps
    );

    bindAllMethods(this);
  }

  public handleConsentChange(analyticsService: any) {
    const hasVisited = localStorage.getItem("hasVisited");

    if (!hasVisited) {
      localStorage.setItem("hasVisited", "false");
    } else if (hasVisited === "false") {
      localStorage.setItem("hasVisited", "true");
    }

    //  callback from mu.js -> must use window variable to access bundle context
    const isWindowDefined = window.WebAnalytics !== undefined;
    const getVisitUUID = isWindowDefined
      ? window.WebAnalytics.getVisitUUID
      : analyticsService.getVisitUUID;
    const getCachedEventsBeforeConsent = isWindowDefined
      ? window.WebAnalytics.getCachedEventsBeforeConsent
      : analyticsService.getCachedEventsBeforeConsent;
    const publishCdmEvents = isWindowDefined
      ? window.WebAnalytics.publishCdmEvents
      : analyticsService.publishCdmEvents;
    const setCachedEventsBeforeConsent = isWindowDefined
      ? window.WebAnalytics.setCachedEventsBeforeConsent
      : analyticsService.setCachedEventsBeforeConsent;

    const isAnalyticsAllowed = getVisitUUID() !== zeroedUUID;
    const visitorUuid = getOptanonConsentObjectValues().optanonConsentId;
    const cachedEvents = getCachedEventsBeforeConsent();

    if (
      visitorUuid !== zeroedUUID &&
      visitorUuid.length > 0 &&
      isAnalyticsAllowed
    ) {
      cachedEvents.forEach((publishObject: PublishCdmEventsProps) => {
        publishCdmEvents(publishObject?.events, publishObject?.options);
      });
    }

    setCachedEventsBeforeConsent([]);
  }

  public isEnabled(): boolean {
    return this._isEnabled;
  }

  public isInited(): boolean {
    return this.inited;
  }

  public async isEnabledWithAllPlugins() {
    try {
      await jWebStates.JWebLibrary;
      await jWebStates.defaultPluginsRegistered;
      await jWebStates.plugins.ValueStore;
      await jWebStates.plugins.EventService;
      await jWebStates.plugins.DataCollection;

      return true;
    } catch (e) {
      const message =
        "Unable to run Analytics. Some JWeb Plugin is missing: " + e;
      this.addToDebuggedEvents(
        this.currentEvent,
        PublishStepsEnum.INIT_ERROR,
        true,
        {
          message: message,
        }
      );
      console?.error(message);
      this._isEnabled = false;
      return false;
    }
  }

  public async init(analyticsProps: AnalyticsServiceInputType): Promise<void> {
    this.addToDebuggedEvents(
      this.currentEvent,
      PublishStepsEnum.INIT_START,
      false
    );
    // INIT BEGIN
    this.mountFrominit(analyticsProps);
    await jWebReady;

    // Get Publisher and Subscriber
    this._publisher = await this._getPublisher();
    this._subscribeEvents();

    // Checking if all JWEB dependences are ready.
    this.isEnabledWithAllPlugins();

    // Initialize Sub-Components
    this._visitUuidHandler.init();

    await this._dataCollectionRecorder.init(this._consentService);

    await this._consentService.init();

    this.inited = true;

    this.addToDebuggedEvents(
      this.currentEvent,
      PublishStepsEnum.INIT_END,
      false,
      {
        stack: this._stack,
        assetType: this.assetType,
        authProvider: this._authProvider,
        visitUuid: this.getVisitUUIDCookieOrZeroed(),
        visitorUuid: getOptanonConsentObjectValues().optanonConsentId,
        serviceWorker: "serviceWorker" in navigator,
      }
    );
  }

  private async _getPublisher(): Promise<GenericPublisher> {
    try {
      // Publisher for WEB
      const eventServicePlugin = await JwebEventService.getEventServicePlugin();
      this.publisherId = `${
        window.location.hostname
      }.wb.${getRandomOrZeroedUUID()}.publisher`;
      const publisherWeb = (await eventServicePlugin.createPublisher(
        this.publisherId
      )) as Publisher;

      return {
        publish: (eventName, eventData) =>
          _publishWeb(publisherWeb, eventName, eventData, this._stack),
      } as GenericPublisher;
    } catch (e) {
      console?.error?.("Analytics: getPublisher Error: ", e);
      this._isEnabled = false;
      return Promise.reject(e);
    }
  }

  public async publishCdmEvents(
    events: DataValveCDMEvent[],
    options?: PublishCdmEventsOptions
  ): Promise<void> {
    try {
      const optInGroups = (window as any)._O?.getOptInGroups();
      const optInGroupsNotStarted =
        optInGroups === "" || optInGroups === undefined;

      if (
        optInGroupsNotStarted &&
        getStratusStack(this._stack) !== Stack.local
      ) {
        this.cachedEventsBeforeConsent.push({ events, options });
        return;
      }

      this.currentEvent = events[0].eventDetail;

      // PUBLISH START
      this.addToDebuggedEvents(
        this.currentEvent,
        PublishStepsEnum.PUBLISH_START,
        false
      );

      // Visit UUID is unlocked, The ValueStore is resolved or rejected.
      await this._visitUuidHandler.isVisitUUIDReady;

      if (hasErrorInEventServicePublisher(this._publisher)) {
        this.addToDebuggedEvents(
          this.currentEvent,
          PublishStepsEnum.PUBLISH_ERROR,
          true,
          { message: "Has Error in Event Service Publisher" }
        );
        return;
      }

      console?.debug?.("Analytics: Publisher was found.");

      await this._writeAnalyticsDataToValueStore(options);
      await this._dataCollectionRecorder.updateDataCollectionService();
      // Only will be applied to Web context

      const token = this._authProvider
        ? decodeToken(await this._authProvider.getAccessToken())
        : null;

      const accountLoginId = token?.sub || undefined;
      const stratusId = accountLoginId ? token?.stratus_id : undefined;

      const trackingIdentifier =
        options?.metadata?.trackingIdentifier || `${getRandomOrZeroedUUID()}`;
      const eventName = DataCollectionEventNames.cdmEvent;
      const eventData: DataValveCDMEventData = {
        events: events as [CDMEventData],
        trackingIdentifier: trackingIdentifier,
        applicationContext: {
          webAppName: window.location.hostname,
          webAppVersion: this._version,
          ucdeCorrelationId: options?.metadata?.xCorrelationId || "",
          webAppConsent: getOptanonConsentObjectValues().optanonConsent,
          visitUuid: this.getVisitUUIDCookieOrZeroed(),
          visitorUuid: getOptanonConsentObjectValues().optanonConsentId,
        },
        ...{
          valveControllerMetadata: {
            accountLoginId: stratusId || options?.metadata?.stratusUserId,
            assetUnit: AssetUnitType.desktop,
            assetType: this.assetType!,
            stratusUserId: stratusId || options?.metadata?.stratusUserId,
            deviceId: options?.metadata?.associatedDeviceUuid,
            edgeType: options?.metadata?.associatedDeviceProductNumber,
          },
        },
      };

      // Publishes event
      console.log("Analytics: publishCdmEvents is calling Publish method. ");
      this.addToDebuggedEvents(
        this.currentEvent,
        PublishStepsEnum.PUBLISH_END,
        false
      );

      await this._publisher.publish(eventName, eventData);
    } catch (e) {
      this.addToDebuggedEvents(
        this.currentEvent,
        PublishStepsEnum.PUBLISH_ERROR,
        true,
        { message: "Analytics: publishCdmEvents Internal Error:" + e }
      );
      console?.error?.("Analytics: publishCdmEvents Internal Error", e);
    }
  }

  private async _writeAnalyticsDataToValueStore(
    options?: PublishCdmEventsOptions
  ): Promise<void> {
    const hostname = window.location.hostname;

    //https://hp-jira.external.hp.com/browse/JSHELL-3536
    await writeDataToValueStore([
      {
        key: ReservedValueStoreKeys.webApplicationName,
        value: hostname,
      },
      {
        key: ReservedValueStoreKeys.webApplicationVersion,
        value: this._version,
      },
      {
        key: ReservedValueStoreKeys.ucdeCorrelationId,
        value: options?.metadata?.xCorrelationId || "",
      },
      {
        key: ReservedValueStoreKeys.stratusUserId,
        value: options?.metadata?.stratusUserId || "",
      },
    ]);
  }

  public addToDebuggedEvents(
    eventDetail: any,
    step: PublishStepsEnum,
    error: boolean,
    context?: any
  ) {
    const newDebugEvent: any = { event: eventDetail, step, error };
    if (context) {
      newDebugEvent.context = context;
    }
    this.debuggedEvents = [...this.debuggedEvents, newDebugEvent];
  }

  public addToSubscribeDebuggedEvents(eventData: any) {
    if (
      this.subscribedEvents.some(
        (item: any) => item.trackingIdentifier === eventData.trackingIdentifier
      )
    ) {
      return;
    }
    this.subscribedEvents = [...this.subscribedEvents, eventData];
  }

  public callbackPublishNotificationError(event: any): void {
    const { eventData } = event || {};

    // Create custom event on splunk RUM
    startSpan?.({
      "workflow.name": "web-analytics-callback-function-result",
      message: JSON.stringify(eventData),
    });

    if (!eventData || eventData.action !== "publishNotification") return;

    const is200Code =
      eventData.actionDetail?.telemetryServiceResponse?.responseCode == 200;

    if (!is200Code) {
      const code =
        eventData.actionDetail?.telemetryServiceResponse?.responseCode;
      // Error case
      const dataToBeSaved = Object.fromEntries(
        getFlatMap(eventData.actionDetail)
      );
      console.error(
        `"Analytics callback: Error ${code} publishing the events."`,
        JSON.stringify(dataToBeSaved)
      );
    }
  }

  public ensureValidCookie = () => {
    const visitUUID = getCookie(visitUuidCookieName);

    if (!visitUUID) {
      this.resetVisitUUIDCookie();
    } else {
      this.renewVisitUUIDCookie(visitUUID, thirtyMinutesInSeconds);
    }
  };

  private renewVisitUUIDCookie(visitUUID: string, age: number) {
    setCookie(visitUuidCookieName, visitUUID, age, false, this.getDomain());
  }

  private resetVisitUUIDCookie() {
    this._visitUuidHandler.init();
  }

  private getDomain() {
    return location.hostname.split(".").slice(-2).join(".");
  }

  private async _subscribeForCmdEventsStatus(): Promise<void> {
    const subscriber = await JwebEventService.getSubscribe();

    subscriber?.subscribe(
      { eventName: DataCollectionEventNames.cdmEventStatus },
      this.callbackPublishNotificationError
    );
  }

  private _subscribeEvents() {
    this._subscribeForCmdEventsStatus();
  }

  public getCachedEventsBeforeConsent(): PublishCdmEventsProps[] {
    return this.cachedEventsBeforeConsent;
  }

  public setCachedEventsBeforeConsent(
    cachedEventsBeforeConsent: PublishCdmEventsProps[]
  ) {
    this.cachedEventsBeforeConsent = cachedEventsBeforeConsent;
  }

  public getDebuggedState() {
    return {
      subscribedEvents: Array.from(new Set(this.subscribedEvents)),
      publishedEvents: this.debuggedEvents,
    };
  }

  public getVisitUUID() {
    // return visitUUID considering the consent approval
    let OptGroups = (window as any)._O?.getOptInGroups();

    if (!OptGroups || OptGroups === "") {
      OptGroups = getOptanonConsentObjectValues().groupsId;
    }

    const consents = OptGroups?.split(",") || []; // returns ['1:1', '2:1', '3:1']
    const consentObj = consents.reduce(
      (accum: { [x: string]: any }, current: string) => {
        const arr = current.split(":");
        accum[arr[0] as keyof {}] = arr[1] as never;
        return accum;
      },
      {
        1: null,
        2: null,
        3: null,
      }
    );

    // Performance & Analytics Cookies
    const isAnalyticsAllowed = !!Number(consentObj["2"]);
    if (!isAnalyticsAllowed) {
      return zeroedUUID;
    } else {
      return this.getVisitUUIDCookieOrZeroed();
    }
  }

  public getVisitUUIDCookieOrZeroed() {
    return getCookie(visitUuidCookieName, false) || zeroedUUID;
  }

  public getConsentId() {
    return getOptanonConsentObjectValues().optanonConsentId;
  }

  public getGroupsId() {
    return getOptanonConsentObjectValues().groupsId;
  }

  public getConsent() {
    return getOptanonConsentObjectValues().optanonConsent;
  }
}
